import { z } from "zod";

/**
 * Схемы событий хаба `/hubs/match-live`.
 *
 * До KAN-988 payload был объявлен как `unknown`, а обработчик приводился к
 * `(...args: unknown[])`. То есть тип события — это обещание разработчика, а не
 * факт: при расхождении контракта фронт падает в неожиданном месте или тихо
 * рисует `undefined`. Схемы сверены с `GoodLuck.Api/Services/MatchLiveService.cs`
 * (09.2026) — именно оттуда уходят эти четыре события.
 *
 * **Режим предупреждения.** Невалидное сообщение логируется, но обрабатывается
 * по-старому: строгая проверка на первом же выкате отбросила бы то, что сейчас
 * «как-то работает», и мы узнали бы об этом от пользователей. Отбрасывание
 * включаем, когда в логе за прод-цикл не окажется невалидных.
 */

/** `MatchStarted`: матч переведён в live. */
export const matchStartedSchema = z.object({
  matchId: z.string(),
  team1Id: z.string().nullable().optional(),
  team2Id: z.string().nullable().optional(),
  bestOf: z.number(),
  round: z.number(),
});

/** `MatchScoreUpdated`: счёт по картам изменился. */
export const matchScoreUpdatedSchema = z.object({
  matchId: z.string(),
  team1Score: z.number(),
  team2Score: z.number(),
});

/** `MatchCompleted`: матч завершён, победитель известен. */
export const matchCompletedSchema = z.object({
  matchId: z.string(),
  winnerId: z.string(),
  team1Score: z.number(),
  team2Score: z.number(),
});

const vetoPickSchema = z.object({
  gameIndex: z.number(),
  pickerTeamId: z.string(),
  side: z.string(),
  pickedAtUtc: z.string(),
});

/** `VetoStateUpdated`: состояние выбора сторон (MatchSideVetoStateDto). */
export const vetoStateUpdatedSchema = z.object({
  matchId: z.string(),
  gameIndex: z.number(),
  pickerTeamId: z.string().nullable().optional(),
  turnDeadlineUtc: z.string().nullable().optional(),
  status: z.string(),
  picks: z.array(vetoPickSchema),
  team1Id: z.string().nullable().optional(),
  team2Id: z.string().nullable().optional(),
});

/** Карта «имя события → схема». Ключи совпадают с именами на бэкенде. */
export const matchLiveEventSchemas = {
  MatchStarted: matchStartedSchema,
  MatchScoreUpdated: matchScoreUpdatedSchema,
  MatchCompleted: matchCompletedSchema,
  VetoStateUpdated: vetoStateUpdatedSchema,
} as const;

/** Имя события хаба — только из этого набора, опечатку ловит компилятор. */
export type MatchLiveEventName = keyof typeof matchLiveEventSchemas;

export type MatchStartedPayload = z.infer<typeof matchStartedSchema>;
export type MatchScoreUpdatedPayload = z.infer<typeof matchScoreUpdatedSchema>;
export type MatchCompletedPayload = z.infer<typeof matchCompletedSchema>;
export type VetoStateUpdatedPayload = z.infer<typeof vetoStateUpdatedSchema>;

/** Типы payload по имени события — выведены из схем, а не написаны руками. */
export type MatchLiveEventPayloads = {
  [K in MatchLiveEventName]: z.infer<(typeof matchLiveEventSchemas)[K]>;
};
